import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { AjustesMarcador, ClimaMarcador, MarcadorCompleto } from "@/types";
import { PadelMarcador } from "@/components/marcador/PadelMarcador";
import { Button } from "@/components/ui/Button";

interface RefereeMatchData {
  match: { id: number; customerName: string };
  club: { id: number; nombre: string };
  court: { id: number; name: string };
  clima: ClimaMarcador;
  needsPlayers: boolean;
  marcador: MarcadorCompleto | null;
}

// Se pide una única vez, justo después de teclear el código: el motor de marcador es propio
// (ver backend/src/lib/padelEngine.ts), así que necesita saber quiénes son los 4 jugadores y
// quién saca primero antes de poder empezar a puntuar.
function PlayerSetupForm({
  onSubmit,
  isPending,
  error,
}: {
  onSubmit: (data: { jugador1: string; jugador2: string; jugador3: string; jugador4: string; saqueInicial: 1 | 2 | 3 | 4 }) => void;
  isPending: boolean;
  error: string | null;
}) {
  const [nombres, setNombres] = useState({ jugador1: "", jugador2: "", jugador3: "", jugador4: "" });
  const [saqueInicial, setSaqueInicial] = useState<1 | 2 | 3 | 4 | null>(null);

  const campos: { key: keyof typeof nombres; equipo: "A" | "B" }[] = [
    { key: "jugador1", equipo: "A" },
    { key: "jugador2", equipo: "A" },
    { key: "jugador3", equipo: "B" },
    { key: "jugador4", equipo: "B" },
  ];
  const todosRellenos = Object.values(nombres).every((n) => n.trim().length > 0);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink-950 p-6 text-white">
      <div className="text-center">
        <h1 className="text-xl font-bold">¿Quiénes juegan?</h1>
        <p className="mt-1 text-sm text-ink-400">Se pide una sola vez, al empezar este partido.</p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (todosRellenos && saqueInicial) onSubmit({ ...nombres, saqueInicial });
        }}
        className="flex w-full max-w-sm flex-col gap-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 text-xs font-medium text-blue-300 uppercase">Equipo A</div>
          {campos
            .filter((c) => c.equipo === "A")
            .map((c) => (
              <input
                key={c.key}
                value={nombres[c.key]}
                onChange={(e) => setNombres((prev) => ({ ...prev, [c.key]: e.target.value }))}
                placeholder="Nombre"
                className="rounded-xl border border-white/20 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-brand-500"
              />
            ))}
          <div className="col-span-2 mt-1 text-xs font-medium text-emerald-300 uppercase">Equipo B</div>
          {campos
            .filter((c) => c.equipo === "B")
            .map((c) => (
              <input
                key={c.key}
                value={nombres[c.key]}
                onChange={(e) => setNombres((prev) => ({ ...prev, [c.key]: e.target.value }))}
                placeholder="Nombre"
                className="rounded-xl border border-white/20 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-brand-500"
              />
            ))}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-ink-400 uppercase">¿Quién saca primero?</p>
          <div className="grid grid-cols-2 gap-2">
            {([1, 2, 3, 4] as const).map((position) => (
              <button
                key={position}
                type="button"
                onClick={() => setSaqueInicial(position)}
                disabled={!nombres[`jugador${position}` as keyof typeof nombres].trim()}
                className={`truncate rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-30 ${
                  saqueInicial === position ? "border-brand-500 bg-brand-500/20" : "border-white/10 bg-white/5 hover:bg-white/10"
                }`}
              >
                {nombres[`jugador${position}` as keyof typeof nombres].trim() || `Jugador ${position}`}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-center text-sm text-red-400">{error}</p>}
        <Button type="submit" className="w-full" disabled={!todosRellenos || !saqueInicial || isPending}>
          Empezar partido
        </Button>
      </form>
    </div>
  );
}

// Pantalla que teclea el árbitro al principio del partido: un código corto, generado desde
// "Partidos en directo", vincula esta sesión a UN partido concreto — la tablet en sí no
// pertenece a ninguna pista fija (ver la nota en deviceView.ts).
function CodeEntryForm({ onSubmit, error }: { onSubmit: (code: string) => void; error: string | null }) {
  const [value, setValue] = useState("");
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink-950 p-6 text-white">
      <div className="text-center">
        <h1 className="text-xl font-bold">Control de partido</h1>
        <p className="mt-1 text-sm text-ink-400">Introduce el código que te ha dado el club para este partido.</p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) onSubmit(value.trim().toUpperCase());
        }}
        className="flex w-full max-w-xs flex-col gap-3"
      >
        <input
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          autoFocus
          maxLength={6}
          placeholder="CÓDIGO"
          className="rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-center text-2xl font-bold tracking-[0.3em] text-white outline-none focus:border-brand-500"
        />
        {error && <p className="text-center text-sm text-red-400">{error}</p>}
        <Button type="submit" className="w-full" disabled={!value.trim()}>
          Entrar
        </Button>
      </form>
    </div>
  );
}


// Tablet de árbitro: el marcador de pista clásico CON controles (botones de winner/forzado/no forzado
// por jugador, menú de la pelota, rueda de ajustes y audio). La pantalla fija de la pista enseña lo
// mismo sin controles (ver DeviceViewPage). El acceso sigue siendo por código de partido.
export function RefereePage() {
  const { code: codeFromUrl } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [entryError, setEntryError] = useState<string | null>(null);
  const queryKey = ["referee-match", codeFromUrl];
  const ocupado = useRef(false);

  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey,
    queryFn: async () => (await api.get<RefereeMatchData>(`/device-view/match/${codeFromUrl}`)).data,
    enabled: !!codeFromUrl,
    refetchInterval: 2000,
    // Un marcador tiene que seguir actualizándose aunque la pestaña no esté en primer plano.
    refetchIntervalInBackground: true,
    retry: false,
  });

  const base = `/device-view/match/${codeFromUrl}`;

  // Cada acción manda su petición y vuelve a pedir el marcador. Mientras hay una en vuelo se ignoran
  // los toques nuevos: un doble toque no puede sumar dos puntos.
  const enviar = useMutation({
    mutationFn: async (p: { ruta: string; body?: unknown }) => {
      if (ocupado.current) return;
      ocupado.current = true;
      try {
        await api.post(`${base}/${p.ruta}`, p.body);
      } finally {
        ocupado.current = false;
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error: unknown) => {
      const mensaje = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(mensaje ?? "No se pudo completar la acción");
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const setPlayers = useMutation({
    mutationFn: (params: { jugador1: string; jugador2: string; jugador3: string; jugador4: string; saqueInicial: 1 | 2 | 3 | 4 }) =>
      api.post(`${base}/players`, params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  if (!codeFromUrl) {
    return (
      <CodeEntryForm
        error={entryError}
        onSubmit={async (code) => {
          setEntryError(null);
          try {
            await api.get(`/device-view/match/${code}`);
            navigate(`/arbitro/${code}`);
          } catch {
            setEntryError("Código no válido o el partido ya ha terminado.");
          }
        }}
      />
    );
  }

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-ink-950 text-sm text-ink-300">Cargando…</div>;
  }

  // Solo se vuelve a pedir el código si nunca llegó a cargar nada. Si falla una consulta suelta (un corte
  // de Wi-Fi), react-query conserva el último marcador y la tablet se queda con él en vez de saltar.
  if (!data) {
    return (
      <CodeEntryForm
        error="Este código ha dejado de funcionar (partido terminado o código incorrecto). Pide uno nuevo."
        onSubmit={(code) => navigate(`/arbitro/${code}`)}
      />
    );
  }

  if (data.needsPlayers) {
    return (
      <PlayerSetupForm
        isPending={setPlayers.isPending}
        error={setPlayers.isError ? "No se pudo guardar. Inténtalo de nuevo." : null}
        onSubmit={(values) => setPlayers.mutate(values)}
      />
    );
  }

  const accion = (ruta: string, body?: unknown) => enviar.mutate({ ruta, body });

  return (
    <div className="fixed inset-0 bg-[#353334]">
      <PadelMarcador
        modo="tablet"
        pista={data.court.name}
        club={data.club.nombre}
        clima={data.clima}
        marcador={data.marcador}
        recibidoEn={dataUpdatedAt}
        controles={{
          onPunto: (position, tipo) => accion("score", { position, tipo }),
          onIniciar: () => accion("start"),
          onRegresar: () => accion("undo"),
          onPausar: () => accion("pause"),
          onReiniciar: () => accion("restart"),
          onResetear: () => accion("reset"),
          onAjustes: (ajustes: Partial<AjustesMarcador>) => accion("settings", ajustes),
          onSaltarDescanso: () => accion("skip-break"),
        }}
      />
      {data.marcador?.estado === "FINALIZADO" && (
        <div className="fixed right-4 bottom-4 z-[70]">
          <Button onClick={() => navigate("/arbitro")}>Controlar otro partido</Button>
        </div>
      )}
    </div>
  );
}
